/**
 * Integrytas Group - Base App
 * Mobile application shell. Reuses the same RBAC menu, page registry and
 * services as the desktop TabManagerShell, re-hung on a mobile spine:
 *   - a compact brand-blue app bar (menu · title · recents · more · avatar)
 *   - a five-slot bottom navigation bar (active destination gets a pill-tinted icon)
 *   - a light slide-out drawer built from the live entitlement menu tree
 *   - Account / More / Recents bottom sheets
 *   - a bespoke Home dashboard; every module reachable & rendered live via
 *     the shared renderPage(registry) inside a mobile scroll container.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Menu as MenuIcon, Search, MoreVertical, ChevronRight, ChevronDown, X,
  Home as HomeIcon, ClipboardCheck, FileText, LayoutGrid, Star, KeyRound,
  LogOut, Repeat, Copy, Plus, Inbox, Bell,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { AuthSession, PageAccess } from '../types/auth';
import type { OpenTab } from '../components/layout/TabManagerShell';
import { apiDb } from '../lib/api_client';
import { useBranding } from '../lib/use_branding';
import { useNotificationState } from '../lib/use_notifications';
import { useApprovalInbox } from './lib/use_approval_inbox';
import { ThemeSelector } from '../components/common/ThemeSelector';
import { Watermark } from '../components/common/Watermark';
import { usePresentationState, PresentationContext } from '../lib/use_presentation';
import { useDdmSchema } from '../lib/ddm_mode';
import { MobileDemoBanner } from './components/MobileDemoBanner';
import { MobileDemoToggle } from './components/MobileDemoToggle';
import { LanguageSwitcher } from '../components/layout/LanguageSwitcher';
import { buildMobileMenuTree, isOpenable, flattenOpenableLeaves, type MobileMenuNode } from './menu_tree';
import { DesktopOnlyModal } from './DesktopOnlyModal';

export interface MobileShellProps {
  session: AuthSession;
  menu: PageAccess[];
  pageLabels: Record<string, string>;
  onSignOut: () => void;
  onChangePassword: () => void;
  onEndMimic?: () => void;
  renderPage: (tab: OpenTab) => React.ReactNode;
}

type PrimaryTab = 'home' | 'approvals' | 'docs' | 'search';
type SheetId = 'account' | 'overflow' | 'recents' | null;

// The Approvals bottom-tab must land on the approver's own inbox — not the first
// leaf that merely mentions tasks/sign-off. Prefer the real inbox, fall back to
// the broader approvals family only if the inbox is not entitled for this user.
const APPROVAL_INBOX_RE = /APR\.INBOX|APPROVAL.?INBOX|MY.?APPROVAL/i;
const APPROVAL_CODE_RE = /APPROV|SIGN.?OFF|MTA.?TASK|REQUEST/i;
const DOCS_CODE_RE = /DMS|DOCUMENT|DOC\.|DOC_|\.DOC|FILE|CONTENT/i;

function initialOf(name: string): string {
  return (name.trim().charAt(0) || '?').toUpperCase();
}

function toTab(node: MobileMenuNode): OpenTab {
  return {
    page_internal_code: node.page.page_internal_code,
    display_text: node.label,
    route_path: node.page.route_path,
  };
}

export function MobileShell({
  session, menu, pageLabels, onSignOut, onChangePassword, onEndMimic, renderPage,
}: MobileShellProps) {
  const { t } = useTranslation();
  const { appName, brandingSuffix } = useBranding();
  const presentation = usePresentationState(session);
  const ddmSchema = useDdmSchema();
  const notifications = useNotificationState(session);
  // Same source as the My Approvals inbox list, so the tab badge always matches it.
  const approvalInbox = useApprovalInbox(session);

  const tree = useMemo(() => buildMobileMenuTree(menu, pageLabels), [menu, pageLabels]);
  const leaves = useMemo(() => flattenOpenableLeaves(tree), [tree]);

  const [primary, setPrimary] = useState<PrimaryTab>('home');
  const [activeModule, setActiveModule] = useState<OpenTab | null>(null);
  const [openModules, setOpenModules] = useState<OpenTab[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sheet, setSheet] = useState<SheetId>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [filter, setFilter] = useState('');
  const [moduleSearch, setModuleSearch] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [desktopModal, setDesktopModal] = useState<string | null>(null);

  const user = session.effective_user;
  const mimicActive = session.mimic.is_active;
  const initials = initialOf(user.display_name || user.email);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast((cur) => (cur === msg ? null : cur)), 1900);
  };

  const closeOverlays = () => { setDrawerOpen(false); setSheet(null); };

  const openModule = (tab: OpenTab) => {
    setActiveModule(tab);
    setOpenModules((prev) =>
      prev.some((m) => m.page_internal_code === tab.page_internal_code)
        ? prev
        : [tab, ...prev].slice(0, 12),
    );
    closeOverlays();
  };

  // Central nav interceptor: a desktop-only page (own flag or inherited from a
  // parent group) opens the "Desktop Version Required" modal instead of routing —
  // the underlying route stays intact. Every menu tap funnels through here.
  const handleOpenNode = (node: MobileMenuNode, labelOverride?: string) => {
    if (node.desktopOnly) { closeOverlays(); setDesktopModal(node.label); return; }
    const tab = toTab(node);
    openModule(labelOverride ? { ...tab, display_text: labelOverride } : tab);
  };

  const goPrimary = (tabId: PrimaryTab) => {
    closeOverlays();
    setPrimary(tabId);
    if (tabId === 'home' || tabId === 'search') { setActiveModule(null); return; }
    if (tabId === 'approvals') {
      // Land on the approver's own inbox; title it "Approvals" so the hosted
      // page header matches the bottom-tab label (one name in both places).
      const node =
        leaves.find((l) => APPROVAL_INBOX_RE.test(l.page.page_internal_code)) ??
        leaves.find((l) => APPROVAL_CODE_RE.test(l.page.page_internal_code));
      if (node) handleOpenNode(node, t('mobile.approvals_tab', 'Approvals'));
      else { setActiveModule(null); showToast(t('shell.no_pages_open', 'No page available')); }
      return;
    }
    // Documents resolve to the first matching live module.
    const hit = leaves.find((l) => DOCS_CODE_RE.test(l.page.page_internal_code));
    if (hit) handleOpenNode(hit);
    else { setActiveModule(null); showToast(t('shell.no_pages_open', 'No page available')); }
  };

  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const closeModule = (code: string) =>
    setOpenModules((prev) => {
      const next = prev.filter((m) => m.page_internal_code !== code);
      if (activeModule?.page_internal_code === code) {
        setActiveModule(null);
        setPrimary('home');
      }
      return next;
    });

  // ---- what the main area shows -------------------------------------------
  const barTitle = activeModule
    ? activeModule.display_text
    : primary === 'home'
      ? t('mobile.home', 'Home')
      : primary === 'search'
        ? t('mobile.search', 'Search')
        : appName;
  const barKicker = activeModule ? t('mobile.module', 'Module') : appName;

  const selectedNav: PrimaryTab | 'more' = activeModule
    ? APPROVAL_CODE_RE.test(activeModule.page_internal_code)
      ? 'approvals'
      : DOCS_CODE_RE.test(activeModule.page_internal_code)
        ? 'docs'
        : 'more'
    : primary;

  // ---- drawer menu rendering ----------------------------------------------
  const filterLower = filter.trim().toLowerCase();
  const filteredLeaves = filterLower
    ? leaves.filter((l) => l.label.toLowerCase().includes(filterLower))
    : null;

  const renderNode = (node: MobileMenuNode, depth: number): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const openablNode = isOpenable(node);
    const isOpen = expanded.has(node.page.page_id);
    return (
      <div key={node.page.page_id}>
        <button
          type="button"
          className={
            'm-dlink' +
            (activeModule?.page_internal_code === node.page.page_internal_code ? ' m-active' : '') +
            (hasChildren && isOpen ? ' m-expanded' : '')
          }
          onClick={() => {
            if (hasChildren) { toggleExpand(node.page.page_id); return; }
            if (openablNode) handleOpenNode(node);
          }}
        >
          {depth === 0 ? <LayoutGrid /> : <ChevronRight />}
          <span className="m-dlabel">{node.label}</span>
          {hasChildren && <span className="m-caret"><ChevronDown /></span>}
        </button>
        {hasChildren && isOpen && (
          <div className="m-dchildren">
            {node.children.map((c) => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    // Mobile mounts the SAME presentation state the desktop shell mounts. This
    // is the shared logic layer, not shared presentation (§16): without a
    // provider, ThemeSelector fell back to the default context, so no ORG/USER
    // theme ever loaded here — which is why the demo theme (and any org theme)
    // never reached mobile at all.
    <PresentationContext.Provider value={presentation}>
    {/* data-ddm lets the stylesheet recolour the mobile chrome on the mirror.
        --m-chrome is a mobile-only token fixed at brand blue and deliberately
        outside the theme system, so the app bar and tab bar follow no theme —
        which left them production-blue in demo mode while everything around
        them turned red. Scoped to demo rather than wired to --color-primary,
        so normal themes keep the chrome they were designed with. */}
    <div className="m-app" data-ddm={ddmSchema}>
      {/* Brand watermark, and the DEMO overlay while on the mirror. Rendered in
          the authenticated shell, not just the loading screen — mobile had it
          only in the `loading` branch, so a signed-in phone showed nothing. */}
      <Watermark appName={appName} brandingSuffix={brandingSuffix} />

      {/* ---- top app bar ---- */}
      <header className="m-appbar">
        <button className="m-iconbtn" aria-label={t('shell.open_menu', 'Open menu')} onClick={() => setDrawerOpen(true)}>
          <MenuIcon />
        </button>
        <div className="m-appbar-title">
          <div className="m-kicker">{barKicker}</div>
          <div className="m-name">{barTitle}</div>
        </div>
        <button className="m-iconbtn" aria-label={t('mobile.open_pages', 'Open pages')} onClick={() => setSheet('recents')}>
          <Copy />
          {openModules.length > 0 && <span className="m-badge">{openModules.length}</span>}
        </button>
        <button className="m-iconbtn" aria-label={t('mobile.more', 'More')} onClick={() => setSheet('overflow')}>
          <MoreVertical />
          {notifications.unreadCount > 0 && <span className="m-badge">{notifications.unreadCount}</span>}
        </button>
        <button className="m-avatar" aria-label={t('shell.user_menu_label', 'Account')} onClick={() => setSheet('account')}>
          {initials}
        </button>
      </header>

      {mimicActive && (
        <div className="m-mimic-flag">
          <Repeat size={14} />
          {t('shell.mimic_active', { name: user.display_name || user.email, defaultValue: 'Acting as another user' })}
        </div>
      )}

      {/* Says which data this session is on. Sits with the mimic flag because
          both answer the same question: "whose data am I looking at?" */}
      <MobileDemoBanner />

      {/* ---- main area ---- */}
      <div className="m-viewport">
        {activeModule ? (
          <div className="m-hosted" key={activeModule.page_internal_code}>
            {renderPage(activeModule)}
          </div>
        ) : primary === 'search' ? (
          <SearchScreen
            leaves={leaves}
            query={moduleSearch}
            onQuery={setModuleSearch}
            onOpen={(n) => handleOpenNode(n)}
          />
        ) : (
          <HomeScreen
            appName={appName}
            userName={user.display_name || user.email}
            roleName={user.roles[0]?.display_text ?? ''}
            notifications={notifications.notifications}
            unread={notifications.unreadCount}
            roots={tree}
            onOpenModule={(n) => handleOpenNode(n)}
            onGo={goPrimary}
            onDrawer={() => setDrawerOpen(true)}
          />
        )}
      </div>

      {/* ---- bottom nav ---- */}
      <nav className="m-tabbar" role="tablist" aria-label={t('mobile.primary_nav', 'Primary')}>
        <NavBtn icon={<HomeIcon />} label={t('mobile.home', 'Home')} active={selectedNav === 'home'} onClick={() => goPrimary('home')} />
        <NavBtn icon={<ClipboardCheck />} label={t('mobile.approvals_tab', 'Approvals')} active={selectedNav === 'approvals'} badge={approvalInbox.count} onClick={() => goPrimary('approvals')} />
        <NavBtn icon={<FileText />} label={t('mobile.docs_nav', 'Docs')} active={selectedNav === 'docs'} onClick={() => goPrimary('docs')} />
        <NavBtn icon={<Search />} label={t('mobile.search', 'Search')} active={selectedNav === 'search'} onClick={() => goPrimary('search')} />
        <NavBtn icon={<LayoutGrid />} label={t('mobile.more', 'More')} active={drawerOpen} onClick={() => setDrawerOpen(true)} />
      </nav>

      {/* ---- overlays ---- */}
      <div className={'m-scrim' + (drawerOpen || sheet ? ' m-open' : '')} onClick={closeOverlays} />

      {/* drawer */}
      <aside className={'m-drawer' + (drawerOpen ? ' m-open' : '')} aria-label={t('mobile.menu', 'Menu')}>
        <div className="m-dhead">
          <div className="m-brand">
            <span className="m-logo">{initialOf(appName)}</span>
            <span>
              <span className="m-bt">{appName}</span>
              <span className="m-bs">{brandingSuffix}</span>
            </span>
          </div>
          <div className="m-dsearch">
            <Search />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('shell.find_a_page', 'Find a page')}
              aria-label={t('shell.find_a_page', 'Find a page')}
            />
          </div>
        </div>
        <div className="m-dnav">
          {filteredLeaves
            ? filteredLeaves.map((l) => (
                <button key={l.page.page_id} type="button" className="m-dlink" onClick={() => handleOpenNode(l)}>
                  <ChevronRight />
                  <span className="m-dlabel">{l.label}</span>
                </button>
              ))
            : tree.map((n) => renderNode(n, 0))}
        </div>
        <div className="m-dfoot">
          <span className="m-av">{initials}</span>
          <span>
            <div className="m-nm">{user.display_name}</div>
            <div className="m-em">{user.email}</div>
          </span>
        </div>
      </aside>

      {/* account sheet */}
      <div className={'m-sheet' + (sheet === 'account' ? ' m-open' : '')} aria-label={t('mobile.account', 'Account')}>
        <div className="m-grabber" />
        <div className="m-sheet-id">
          <span className="m-av">{initials}</span>
          <span>
            <div className="m-nm">{user.display_name}</div>
            <div className="m-em">{user.email}</div>
          </span>
        </div>
        {mimicActive && onEndMimic && (
          <button className="m-sheet-item" onClick={() => { closeOverlays(); onEndMimic(); }}>
            <Repeat /> {t('shell.mimic_exit', 'Exit mimic session')}
          </button>
        )}
        <button className="m-sheet-item" onClick={() => { closeOverlays(); onChangePassword(); }}>
          <KeyRound /> {t('auth.change_password', 'Change password')}
        </button>
        <div className="m-sheet-sep" />
        <button className="m-sheet-item m-danger" onClick={() => { closeOverlays(); onSignOut(); }}>
          <LogOut /> {t('auth.sign_out', 'Sign out')}
        </button>
      </div>

      {/* overflow sheet */}
      <div className={'m-sheet' + (sheet === 'overflow' ? ' m-open' : '')} aria-label={t('mobile.more', 'More')}>
        <div className="m-grabber" />
        <div className="m-sheet-h">{t('mobile.notifications', 'Notifications')}</div>
        <button className="m-sheet-item" onClick={() => { setSheet(null); setPrimary('search'); }}>
          <Bell /> {t('mobile.notifications', 'Notifications')}
          {notifications.unreadCount > 0 && <span className="m-rt">{notifications.unreadCount}</span>}
        </button>
        <div className="m-sheet-sep" />
        <div className="m-sheet-h">{t('mobile.preferences', 'Preferences')}</div>
        <div className="m-sheet-item" style={{ justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}><LayoutGrid /> {t('mobile.theme', 'Theme')}</span>
          <ThemeSelector />
        </div>
        <div className="m-sheet-item" style={{ justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 14 }}><LayoutGrid /> {t('mobile.language', 'Language')}</span>
          <LanguageSwitcher session={session} />
        </div>
        <MobileDemoToggle />
      </div>

      {/* recents sheet */}
      <div className={'m-sheet' + (sheet === 'recents' ? ' m-open' : '')} aria-label={t('mobile.open_pages', 'Open pages')}>
        <div className="m-grabber" />
        <div className="m-sheet-h">{t('mobile.open_pages', 'Open pages')}{openModules.length > 0 ? ` · ${openModules.length}` : ''}</div>
        {openModules.length === 0 ? (
          <div className="m-empty"><Inbox /> {t('mobile.no_open_pages', 'No pages open yet')}</div>
        ) : (
          <div className="m-list">
            {openModules.map((m) => (
              <div
                key={m.page_internal_code}
                className="m-row"
                onClick={() => { setSheet(null); setActiveModule(m); }}
              >
                <span className="m-lead m-l-blue"><FileText /></span>
                <span className="m-body">
                  <span className="m-h">{m.display_text}</span>
                  <span className="m-s">{m.page_internal_code}</span>
                </span>
                <button
                  className="m-iconbtn"
                  style={{ minWidth: 36, height: 36, color: 'var(--color-text-muted)' }}
                  aria-label={t('mobile.close', 'Close')}
                  onClick={(e) => { e.stopPropagation(); closeModule(m.page_internal_code); }}
                >
                  <X />
                </button>
              </div>
            ))}
          </div>
        )}
        <button
          className="m-sheet-item"
          style={{ justifyContent: 'center', color: 'var(--color-primary)', fontWeight: 700 }}
          onClick={() => { setSheet(null); setPrimary('search'); setActiveModule(null); }}
        >
          <Plus /> {t('mobile.open_new_page', 'Open a page')}
        </button>
      </div>

      {/* toast */}
      <div className={'m-toast' + (toast ? ' m-show' : '')}>
        {toast && <><Star /> <span>{toast}</span></>}
      </div>

      {/* desktop-only interception modal */}
      <DesktopOnlyModal
        open={desktopModal !== null}
        pageTitle={desktopModal ?? undefined}
        onDismiss={() => setDesktopModal(null)}
      />
    </div>
    </PresentationContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Bottom-nav button
// ---------------------------------------------------------------------------
function NavBtn({ icon, label, active, badge, onClick }: {
  icon: React.ReactNode; label: string; active: boolean; badge?: number; onClick: () => void;
}) {
  const count = badge && badge > 0 ? badge : 0;
  return (
    <button type="button" className="m-tab" role="tab" aria-selected={active} onClick={onClick}>
      <span className="m-ind" />
      <span className="m-tab-ic">
        {icon}
        {count > 0 && <span className="m-tab-badge">{count > 99 ? '99+' : count}</span>}
      </span>
      <span className="m-tab-lb">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// KPI tile (dashboard metric)
// ---------------------------------------------------------------------------
function KpiTile({ label, value, tone }: { label: string; value: number | null; tone?: 'alert' }) {
  return (
    <div className={'m-kpi' + (tone === 'alert' ? ' m-kpi-alert' : '')}>
      <div className="m-kpi-l">{label}</div>
      <div className="m-kpi-v">{value == null ? '—' : value.toLocaleString()}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Home dashboard (bespoke)
// ---------------------------------------------------------------------------
function HomeScreen({
  appName, userName, roleName, notifications, unread, roots, onOpenModule, onGo, onDrawer,
}: {
  appName: string;
  userName: string;
  roleName: string;
  notifications: { title: string; body?: string | null; created_at?: string; is_read: boolean }[];
  unread: number;
  roots: MobileMenuNode[];
  onOpenModule: (n: MobileMenuNode) => void;
  onGo: (tab: PrimaryTab) => void;
  onDrawer: () => void;
}) {
  const { t } = useTranslation();
  const firstName = userName.split(/[\s@]/)[0] || userName;
  const recent = notifications.slice(0, 4);
  const quickRoots = roots.slice(0, 6);

  // Live KPI counts over core CSM tables. RLS applies via the gateway JWT, so the
  // numbers reflect what this user may see. Best-effort: on failure the tiles show
  // "—" rather than an error notice (errors-only, §17.B7).
  const [metrics, setMetrics] = useState<{ users: number | null; roles: number | null; pages: number | null }>(
    { users: null, roles: null, pages: null },
  );
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [u, r, p] = await Promise.all([
          apiDb.from('csm_user_master').select('user_id', { head: true, count: 'exact' }).eq('is_active', 'Y'),
          apiDb.from('csm_role_master').select('role_id', { head: true, count: 'exact' }).eq('is_active', 'Y'),
          apiDb.from('csm_page_master').select('page_id', { head: true, count: 'exact' }).eq('is_active', 'Y'),
        ]);
        if (!cancelled) setMetrics({ users: u.count ?? 0, roles: r.count ?? 0, pages: p.count ?? 0 });
      } catch {
        /* best-effort metrics — leave as "—" */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="m-screen">
      <div className="m-greet">
        <div className="m-hi">{t('mobile.greeting', { name: firstName, defaultValue: `Hello, ${firstName}` })}</div>
        <div className="m-sub">{appName}{roleName ? ' · ' + roleName : ''}</div>
      </div>

      <div className="m-kpis">
        <KpiTile label={t('mobile.kpi.users', 'Active users')} value={metrics.users} />
        <KpiTile label={t('mobile.kpi.roles', 'Roles')} value={metrics.roles} />
        <KpiTile label={t('mobile.kpi.pages', 'Pages')} value={metrics.pages} />
        <KpiTile label={t('mobile.kpi.unread', 'Unread')} value={unread} tone={unread > 0 ? 'alert' : undefined} />
      </div>

      {unread > 0 && (
        <button className="m-attention" onClick={() => onGo('search')}>
          <span className="m-ring">{unread}</span>
          <span className="m-att-t">
            <span className="m-l1">{t('mobile.unread_notifications', { n: unread, defaultValue: `${unread} unread notifications` })}</span>
            <span className="m-l2">{t('mobile.tap_to_review', 'Tap to review')}</span>
          </span>
          <ChevronRight />
        </button>
      )}

      <div className="m-sect"><h2>{t('mobile.quick_actions', 'Quick actions')}</h2></div>
      <div className="m-quick">
        <button onClick={() => onGo('approvals')}><span className="m-ic"><ClipboardCheck /></span><span className="m-lb">{t('mobile.approvals_tab', 'Approvals')}</span></button>
        <button onClick={() => onGo('docs')}><span className="m-ic"><FileText /></span><span className="m-lb">{t('mobile.documents', 'Documents')}</span></button>
        <button onClick={onDrawer}><span className="m-ic"><LayoutGrid /></span><span className="m-lb">{t('mobile.modules', 'Modules')}</span></button>
        <button onClick={() => onGo('search')}><span className="m-ic"><Search /></span><span className="m-lb">{t('mobile.search', 'Search')}</span></button>
      </div>

      {quickRoots.length > 0 && (
        <>
          <div className="m-sect">
            <h2>{t('mobile.your_modules', 'Your modules')}</h2>
            <button className="m-link" onClick={onDrawer}>{t('mobile.all', 'All')}</button>
          </div>
          <div className="m-card"><div className="m-list">
            {quickRoots.map((n) => {
              const leaf = isOpenable(n);
              return (
                <button
                  key={n.page.page_id}
                  className="m-row"
                  onClick={() => (leaf ? onOpenModule(n) : onDrawer())}
                >
                  <span className="m-lead m-l-slate"><LayoutGrid /></span>
                  <span className="m-body">
                    <span className="m-h">{n.label}</span>
                    <span className="m-s">{n.children.length > 0 ? t('mobile.n_items', { n: n.children.length, defaultValue: `${n.children.length} items` }) : n.page.page_internal_code}</span>
                  </span>
                  <span className="m-chev"><ChevronRight /></span>
                </button>
              );
            })}
          </div></div>
        </>
      )}

      <div className="m-sect"><h2>{t('mobile.recent_activity', 'Recent activity')}</h2></div>
      {recent.length === 0 ? (
        <div className="m-card"><div className="m-empty"><Inbox /> {t('mobile.no_activity', 'Nothing recent')}</div></div>
      ) : (
        <div className="m-card"><div className="m-list">
          {recent.map((n, i) => (
            <div className="m-row" key={i}>
              <span className={'m-lead ' + (n.is_read ? 'm-l-slate' : 'm-l-blue')}><Bell /></span>
              <span className="m-body">
                <span className="m-h">{n.title}</span>
                {n.body && <span className="m-s">{n.body}</span>}
              </span>
            </div>
          ))}
        </div></div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Search screen (bespoke) — searches the live entitlement menu
// ---------------------------------------------------------------------------
function SearchScreen({ leaves, query, onQuery, onOpen }: {
  leaves: MobileMenuNode[];
  query: string;
  onQuery: (q: string) => void;
  onOpen: (n: MobileMenuNode) => void;
}) {
  const { t } = useTranslation();
  const q = query.trim().toLowerCase();
  const results = q
    ? leaves.filter((l) => l.label.toLowerCase().includes(q) || l.page.page_internal_code.toLowerCase().includes(q)).slice(0, 40)
    : leaves.slice(0, 20);
  return (
    <section className="m-screen">
      <div className="m-searchbox">
        <Search />
        <input
          autoFocus
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t('omnibox.search_hint', 'Search')}
          aria-label={t('omnibox.search_hint', 'Search')}
        />
      </div>
      <div className="m-sect"><h2>{q ? t('mobile.results', 'Results') : t('mobile.jump_to', 'Jump to')}</h2></div>
      {results.length === 0 ? (
        <div className="m-card"><div className="m-empty"><Search /> {t('mobile.no_results', 'No matches')}</div></div>
      ) : (
        <div className="m-card"><div className="m-list">
          {results.map((l) => (
            <button key={l.page.page_id} className="m-row" onClick={() => onOpen(l)}>
              <span className="m-lead m-l-slate"><FileText /></span>
              <span className="m-body">
                <span className="m-h">{l.label}</span>
                <span className="m-s">{l.page.page_internal_code}</span>
              </span>
              <span className="m-chev"><ChevronRight /></span>
            </button>
          ))}
        </div></div>
      )}
    </section>
  );
}
